// Jump in (2026-11-01, the landing's live hero; Sheep Tag's src/client/jumpin.ts). The landing shows a real bot match
// (the game itself in a frame, /play/?live=1, or on phones the filmed loop), and the sim is deterministic per seed, so
// Jump in hands that exact match to /play:
//   /play/?jump=<seat>&seed=<n>&tick=<n>[&armies=a,b,c,d][&h=<hash>][&cam=<x>,<z>,<dist>] and the hero's sky pins
// /play rebuilds it from the same setup (jumpmatch.ts), fast-forwards to the tick behind a cover, and you take that
// corner from its bot (net/lockstep.ts humanTakes, the swap a room makes when a person comes back to a seat). h is the
// hero's sim hash at the tick, so /play can tell whether it arrived at the same match. seat is the corner the hero's
// camera was on (jumpmatch.ts seatInView), or "any" for the strongest one still standing (the loop's fixed camera).
// Pure and import-free (the landing bundles it). Validated strictly: a jump with a number out of range is no jump, and
// the page opens as it would without it.

/** The sky the hero is shown in, the loop's (scripts/hero-loop.ts): /play keeps it, so the jump opens on the same look. */
export const HERO_SKY = { season: 'summer', weather: 'clear', tod: 'midday' } as const;
/** Every address key a jump uses, taken off the address once it has started (and not a test visit, stats.ts). */
export const JUMP_KEYS = ['jump', 'seed', 'tick', 'armies', 'h', 'cam', ...Object.keys(HERO_SKY)] as const;
/** The longest match a jump reaches into: 30 minutes (a match ends by the hard end, well inside it). */
export const MAX_TICK = 30 * 60 * 20;

export interface Jump {
  /** The corner to take, or null for the strongest one still standing. */
  seat: number | null;
  seed: number;
  tick: number;
  /** One army per seat (main.ts ?armies=), or empty: the bots pick theirs from the seed. */
  armies: string[];
  hash: string | null;
  /** The hero camera's look point and distance (main.ts CameraRig), or null. */
  cam: [number, number, number] | null;
}

const whole = (v: string | null, hi: number) => (v !== null && /^\d{1,9}$/.test(v) && Number(v) <= hi ? Number(v) : null);

export function parseJump(q: URLSearchParams): Jump | null {
  const seatRaw = q.get('jump');
  if (seatRaw === null || q.getAll('jump').length !== 1 || q.has('room') || q.has('capture') || q.has('live') || q.get('title') === '1') return null;
  const seat = seatRaw === 'any' ? null : whole(seatRaw, 3);
  const seed = whole(q.get('seed'), 999_999_999), tick = whole(q.get('tick'), MAX_TICK);
  if ((seat === null && seatRaw !== 'any') || seed === null || tick === null) return null;
  const a = q.get('armies');
  if (a !== null && !/^[a-z]{1,12}(,[a-z]{1,12}){0,3}$/.test(a)) return null;
  const h = q.get('h'), c = q.get('cam')?.split(',').map(Number);
  return {
    seat, seed, tick, armies: a ? a.split(',') : [],
    hash: h && /^[0-9a-f]{1,8}$/.test(h) ? h : null,
    cam: c && c.length === 3 && c.every((v) => Number.isFinite(v) && v >= 0 && v <= 400) ? [c[0], c[1], c[2]] : null,
  };
}

/** The link Jump in points at, for this moment of the hero's match. */
export function jumpHref(j: Jump): string {
  const q = new URLSearchParams({ jump: j.seat === null ? 'any' : String(j.seat), seed: String(j.seed), tick: String(j.tick) });
  if (j.armies.length) q.set('armies', j.armies.join(','));
  if (j.hash) q.set('h', j.hash);
  if (j.cam) q.set('cam', j.cam.map((v) => v.toFixed(1)).join(','));
  for (const [k, v] of Object.entries(HERO_SKY)) q.set(k, v);
  // the landing's hero button (src/stats/schema.ts viaOf): a jump that fails to parse still counts as from the hero
  q.set('src', 'hero');
  return `/play/?${q}`;
}
